import type { ICellRendererParams, IHeaderParams } from 'ag-grid-community';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useSecurity } from '../../../../hooks';
import type { MarketDataSnapshotLevel } from '../../../../types';
import type { Security } from '../../../../types/Security';
import { getSpreadUnit } from '../../../../utils/security';
import { getSpreadDisplayValue } from '../../../../utils/spread';
import { Button, ButtonVariants } from '../../../Button';
import { HStack } from '../../../Core';
import { FormControlSizes } from '../../../Form';
import { InlineFormattedNumber, NumberVariants } from '../../../FormattedNumber';
import { Text } from '../../../Text';
import { DotAlignedPrice } from './DotAlignedPrice';
import { getSecurityMarketDataBidsAndOffers } from './helpers';
import {
  PriceUnit,
  PriceWithUnit,
  SecurityMarketDataActionsSlot,
  SecurityMarketDataBidPriceColumn,
  SecurityMarketDataCellGrid,
  SecurityMarketDataOfferPriceColumn,
  SecurityMarketDataPriceRowGrid,
  SecurityMarketDataPricesSlot,
  SpreadPill,
  SpreadSlot,
} from './styles';
import type {
  SecurityMarketDataCellPriceActions,
  SecurityMarketDataCellValue,
  SecurityMarketDataHeaderSettingsButton,
} from './types';

function SecurityMarketDataPriceRow({
  bid,
  highlightBid,
  highlightOffer,
  offer,
  alignPricesOnDecimal = false,
  isPricingModeBPS = false,
  priceIncrement: priceIncrementOverride,
  security,
  priceActions,
}: {
  bid?: MarketDataSnapshotLevel;
  highlightBid?: boolean;
  highlightOffer?: boolean;
  offer?: MarketDataSnapshotLevel;
  alignPricesOnDecimal?: boolean;
  isPricingModeBPS?: boolean;
  priceIncrement?: string;
  security?: Security;
  priceActions?: SecurityMarketDataCellPriceActions;
}) {
  const displayBid = isPricingModeBPS ? (bid?.BPS ? { ...bid, Price: bid.BPS } : undefined) : bid;
  const displayOffer = isPricingModeBPS ? (offer?.BPS ? { ...offer, Price: offer.BPS } : undefined) : offer;
  const spreadDisplay = useMemo(() => {
    if (!displayBid?.Price || !displayOffer?.Price) {
      return undefined;
    }

    const value = getSpreadDisplayValue(
      displayBid.Price,
      displayOffer.Price,
      getSpreadUnit(security, isPricingModeBPS),
      isPricingModeBPS
    );
    return value.length > 0 ? value : undefined;
  }, [displayBid?.Price, displayOffer?.Price, isPricingModeBPS, security]);
  const priceIncrement = priceIncrementOverride ?? security?.DefaultPriceIncrement;
  const priceDisplaySpec = security?.PriceDisplaySpec;

  return (
    <SecurityMarketDataPriceRowGrid>
      <SecurityMarketDataBidPriceColumn
        $highlightVariant={highlightBid ? 'bid' : undefined}
        clickable={Boolean(displayBid?.Price && priceActions?.onBidPriceClick)}
        data-highlighted={highlightBid ? 'true' : undefined}
        data-testid="security-market-data-bid-price"
        onClick={event => {
          if (!displayBid?.Price || !priceActions?.onBidPriceClick) {
            return;
          }
          event.stopPropagation();
          priceActions.onBidPriceClick(displayBid);
        }}
        onContextMenu={event => {
          if (!bid?.Price || !priceActions?.onBidPriceContextMenu) {
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          priceActions.onBidPriceContextMenu(bid);
        }}
        onMouseLeave={priceActions?.onBidPriceMouseLeave}
      >
        {displayBid?.Price && alignPricesOnDecimal ? (
          <PriceWithUnit $align="left">
            <DotAlignedPrice number={displayBid.Price} increment={priceIncrement} variant={NumberVariants.Positive} />
            {isPricingModeBPS ? <PriceUnit>BPS</PriceUnit> : null}
          </PriceWithUnit>
        ) : displayBid?.Price ? (
          <PriceWithUnit $align="left">
            <InlineFormattedNumber
              number={displayBid.Price}
              increment={priceIncrement}
              specification={priceDisplaySpec}
              variant={NumberVariants.Positive}
              align="left"
              round
            />
            {isPricingModeBPS ? <PriceUnit>BPS</PriceUnit> : null}
          </PriceWithUnit>
        ) : (
          <Text>-</Text>
        )}
      </SecurityMarketDataBidPriceColumn>
      {spreadDisplay ? (
        <SpreadPill data-testid="security-market-data-spread">{spreadDisplay}</SpreadPill>
      ) : (
        <SpreadSlot aria-hidden />
      )}
      <SecurityMarketDataOfferPriceColumn
        $highlightVariant={highlightOffer ? 'offer' : undefined}
        clickable={Boolean(displayOffer?.Price && priceActions?.onOfferPriceClick)}
        data-highlighted={highlightOffer ? 'true' : undefined}
        data-testid="security-market-data-offer-price"
        onClick={event => {
          if (!displayOffer?.Price || !priceActions?.onOfferPriceClick) {
            return;
          }
          event.stopPropagation();
          priceActions.onOfferPriceClick(displayOffer);
        }}
        onContextMenu={event => {
          if (!offer?.Price || !priceActions?.onOfferPriceContextMenu) {
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          priceActions.onOfferPriceContextMenu(offer);
        }}
        onMouseLeave={priceActions?.onOfferPriceMouseLeave}
      >
        {displayOffer?.Price && alignPricesOnDecimal ? (
          <PriceWithUnit $align="right">
            <DotAlignedPrice number={displayOffer.Price} increment={priceIncrement} variant={NumberVariants.Negative} />
            {isPricingModeBPS ? <PriceUnit>BPS</PriceUnit> : null}
          </PriceWithUnit>
        ) : displayOffer?.Price ? (
          <PriceWithUnit $align="right">
            <InlineFormattedNumber
              number={displayOffer.Price}
              increment={priceIncrement}
              specification={priceDisplaySpec}
              variant={NumberVariants.Negative}
              align="right"
              round
            />
            {isPricingModeBPS ? <PriceUnit>BPS</PriceUnit> : null}
          </PriceWithUnit>
        ) : (
          <Text>-</Text>
        )}
      </SecurityMarketDataOfferPriceColumn>
    </SecurityMarketDataPriceRowGrid>
  );
}

export function SecurityMarketDataHeader(
  params: IHeaderParams & { settingsButton: SecurityMarketDataHeaderSettingsButton }
) {
  const { displayName, settingsButton } = params;
  return (
    <HStack w="100%" gap="spacing4" justifyContent="space-between">
      <Text>{displayName}</Text>
      <Button
        size={FormControlSizes.Tiny}
        variant={ButtonVariants.Default}
        onClick={event => {
          event.stopPropagation();
          settingsButton.onClick(params);
        }}
      >
        {settingsButton.label}
      </Button>
    </HStack>
  );
}

type SecurityMarketDataCellProps = Pick<ICellRendererParams<unknown, SecurityMarketDataCellValue>, 'value'> & {
  alignPricesOnDecimal?: boolean;
  actions?: ReactNode;
  isPricingModeBPS?: boolean;
  priceIncrement?: string;
  priceActions?: SecurityMarketDataCellPriceActions;
};

export function SecurityMarketDataCell({
  value,
  alignPricesOnDecimal,
  actions,
  isPricingModeBPS,
  priceIncrement,
  priceActions,
}: SecurityMarketDataCellProps) {
  const security = useSecurity(value?.marketData?.Symbol);
  const bidsAndOffers = getSecurityMarketDataBidsAndOffers(value);

  return (
    <SecurityMarketDataCellGrid>
      <SecurityMarketDataPricesSlot>
        {bidsAndOffers.map(([bid, offer], index) => (
          <SecurityMarketDataPriceRow
            key={index}
            bid={bid}
            offer={offer}
            alignPricesOnDecimal={alignPricesOnDecimal}
            isPricingModeBPS={isPricingModeBPS}
            priceIncrement={priceIncrement}
            security={security}
            priceActions={priceActions}
            highlightBid={value?.highlightBid}
            highlightOffer={value?.highlightOffer}
          />
        ))}
      </SecurityMarketDataPricesSlot>
      {actions ? <SecurityMarketDataActionsSlot>{actions}</SecurityMarketDataActionsSlot> : null}
    </SecurityMarketDataCellGrid>
  );
}
